// Distribution: effective serving state with reasons (from the server), the
// permanent Tag, the active version (publish/rollback) and client controls.
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Archive, Pause, Pencil, Play } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, call } from "@/api/client";
import { keys } from "@/api/hooks";
import { formatDateTime } from "@/lib/format";
import { displayFormat } from "@/lib/labels";
import { Button } from "@/ui/button";
import { ConfirmDialog } from "@/ui/dialog";
import { Field } from "@/ui/field";
import { NativeSelect } from "@/ui/input";
import { Callout, Details, PageHeader, Panel, Section } from "@/ui/layout";
import { NameDialog } from "@/components/name-dialog";
import { ServingReasons, ServingStateBadge } from "@/components/status";
import { TagCard } from "@/components/tag-card";
import { Breadcrumbs } from "../common/breadcrumbs";
import { notifyError } from "../common/notify";
import { PageError, PageLoading, useTitle, useRouteParams, useSearchRecord } from "../common/page";
import { useCreative, useDistribution, useTag, useVersions } from "../common/queries";
import { CompatibilityBadge, CompatibilitySummary } from "../platforms/compatibility-summary";
import { useDistributionCompatibility } from "../platforms/queries";

export function DistributionDetailPage() {
  const { orgId, distributionId } = useRouteParams("orgId", "distributionId");
  const search = useSearchRecord();
  const queryClient = useQueryClient();
  const distribution = useDistribution(orgId, distributionId);
  const tag = useTag(orgId, distributionId);
  const creativeId = distribution.data?.creativeId ?? "";
  const creative = useCreative(orgId, creativeId);
  const versions = useVersions(orgId, creativeId);
  const compatibility = useDistributionCompatibility(orgId, distributionId);
  const [pending, setPending] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [confirm, setConfirm] = useState<null | "archive" | { versionId: string; versionNumber: number }>(null);
  const [target, setTarget] = useState("");
  useTitle(distribution.data?.name);

  if (distribution.isLoading) return <PageLoading />;
  if (distribution.error || !distribution.data) return <PageError error={distribution.error} orgId={orgId} />;
  const d = distribution.data;
  const published = versions.items.filter((v) => v.state === "PUBLISHED");

  const act = async (run: () => Promise<unknown>, success: string) => {
    setPending(true);
    try {
      await run();
      toast.success(success);
      await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
    } catch (problem) {
      notifyError(problem);
    } finally {
      setPending(false);
      setConfirm(null);
    }
  };
  const path = { params: { path: { orgId, distributionId } } };

  return (
    <div className="grid gap-6">
      <Breadcrumbs
        orgId={orgId}
        clientId={creative.data?.clientId}
        brandId={creative.data?.brandId}
        campaignId={d.campaignId}
        current={d.name}
      />
      <PageHeader
        eyebrow={
          <span>
            Distribución de{" "}
            <Link to={`/o/${orgId}/creatives/${d.creativeId}`} className="underline-offset-2 hover:underline">
              {creative.data?.name ?? "creativo"}
            </Link>
          </span>
        }
        title={
          <span className="flex flex-wrap items-center gap-2">
            {d.name} <ServingStateBadge state={d.serving.effectiveState} />
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-x-2">
            <span>
              {d.platform.name} · {displayFormat(d.format)}
              {d.platform.kind === "custom" ? " · Plataforma personalizada" : ""}
            </span>
            <CompatibilityBadge status={d.compatibility.overall} />
          </span>
        }
        actions={
          d.canManage && !d.archived ? (
            <>
              <Button
                size="sm"
                onClick={() => {
                  setRenameOpen(true);
                }}
              >
                <Pencil /> Renombrar
              </Button>
              {d.desiredState === "ACTIVE" ? (
                <Button
                  size="sm"
                  disabled={pending}
                  onClick={() =>
                    void act(
                      () => call(() => api.POST("/orgs/{orgId}/distributions/{distributionId}/pause", path)),
                      "Distribución pausada"
                    )
                  }
                >
                  <Pause /> Pausar
                </Button>
              ) : (
                <Button
                  size="sm"
                  disabled={pending}
                  onClick={() =>
                    void act(
                      () => call(() => api.POST("/orgs/{orgId}/distributions/{distributionId}/resume", path)),
                      "Distribución reanudada"
                    )
                  }
                >
                  <Play /> Reanudar
                </Button>
              )}
              <Button
                size="sm"
                variant="danger-outline"
                disabled={pending}
                onClick={() => {
                  setConfirm("archive");
                }}
              >
                <Archive /> Archivar
              </Button>
            </>
          ) : null
        }
      />
      {search.created ? (
        <Callout tone="success" title="Distribución creada">
          Esta es su Tag permanente. La plataforma y el formato ya no cambian: para usar el creativo en otra plataforma,
          crea otra distribución.
        </Callout>
      ) : null}
      {search.published ? (
        <Callout tone="success" title="Publicado">
          Tu versión ya está activa en esta distribución. Este es tu Tag permanente.
        </Callout>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        <div className="grid content-start gap-6">
          <Section
            title="Estado de entrega"
            description="Calculado por InteractAds a partir de pausas, retenciones y la versión activa."
          >
            <ServingReasons reasons={d.serving.reasons} />
          </Section>
          {tag.data ? (
            <TagCard tag={tag.data} serving={d.serving.effectiveState === "SERVING"} platformName={d.platform.name} />
          ) : null}
        </div>
        <div className="grid content-start gap-6">
          <Section
            title="Compatibilidad"
            description="Según la evidencia de InteractAds para esta plataforma, producto y formato."
          >
            {compatibility.data ? (
              <CompatibilitySummary orgId={orgId} assessment={compatibility.data} canRequestReview={d.canManage} />
            ) : (
              <p className="text-sm text-muted">Consultando compatibilidad…</p>
            )}
          </Section>
          <Section title="Versión activa">
            <Panel className="grid gap-3 p-4">
              <p className="text-sm">
                {d.activeVersion ? (
                  <Link to={`/o/${orgId}/versions/${d.activeVersion.id}`} className="font-medium hover:underline">
                    Versión {d.activeVersion.versionNumber}
                  </Link>
                ) : (
                  <span className="text-muted">Ninguna versión publicada activa.</span>
                )}
              </p>
              {d.canManage && !d.archived && published.length > 0 ? (
                <form
                  className="grid gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const v = published.find((x) => x.id === target);
                    if (v) setConfirm({ versionId: v.id, versionNumber: v.versionNumber });
                  }}
                >
                  <Field
                    label="Cambiar a otra versión publicada"
                    hint="Úsalo para volver a una versión anterior. El Tag no cambia."
                  >
                    <NativeSelect
                      value={target}
                      onChange={(e) => {
                        setTarget(e.target.value);
                      }}
                    >
                      <option value="">Selecciona…</option>
                      {published
                        .filter((v) => v.id !== d.activeVersion?.id)
                        .map((v) => (
                          <option key={v.id} value={v.id}>
                            Versión {v.versionNumber} · publicada {formatDateTime(v.publishedAt)}
                          </option>
                        ))}
                    </NativeSelect>
                  </Field>
                  <Button type="submit" size="sm" disabled={target === "" || pending}>
                    Activar versión
                  </Button>
                </form>
              ) : null}
            </Panel>
          </Section>
          <Section title="Detalles">
            <Details
              items={[
                { label: "Plataforma", value: d.platform.name },
                { label: "Formato", value: displayFormat(d.format) },
                { label: "Intención", value: d.desiredState === "ACTIVE" ? "Activa" : "Pausada por tu equipo" },
                { label: "Revisión de configuración", value: <span className="tabular">#{d.manifestSequence}</span> },
                { label: "Creada", value: formatDateTime(d.createdAt) },
                { label: "Archivada", value: d.archived ? formatDateTime(d.archivedAt) : "No" },
              ]}
            />
          </Section>
        </div>
      </div>

      <NameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        title="Renombrar distribución"
        label="Nombre"
        initial={d.name}
        submitLabel="Guardar"
        onSubmit={async (name) => {
          await call(() => api.PATCH("/orgs/{orgId}/distributions/{distributionId}", { ...path, body: { name } }));
          await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
        }}
      />
      <ConfirmDialog
        open={confirm === "archive"}
        onOpenChange={(open) => {
          if (!open) setConfirm(null);
        }}
        title="¿Archivar esta distribución?"
        description="Dejará de entregarse de forma definitiva y no podrá reactivarse. Su historial se conserva."
        confirmLabel="Archivar"
        destructive
        pending={pending}
        onConfirm={() =>
          void act(
            () => call(() => api.POST("/orgs/{orgId}/distributions/{distributionId}/archive", path)),
            "Distribución archivada"
          )
        }
      />
      <ConfirmDialog
        open={typeof confirm === "object" && confirm !== null}
        onOpenChange={(open) => {
          if (!open) setConfirm(null);
        }}
        title={
          typeof confirm === "object" && confirm !== null ? `¿Activar la versión ${String(confirm.versionNumber)}?` : ""
        }
        description="La distribución pasará a entregar esa versión publicada. El Tag permanente no cambia."
        confirmLabel="Activar versión"
        pending={pending}
        onConfirm={() => {
          if (typeof confirm === "object" && confirm !== null) {
            void act(
              () =>
                call(() =>
                  api.PUT("/orgs/{orgId}/distributions/{distributionId}/active-version", {
                    ...path,
                    body: { versionId: confirm.versionId },
                  })
                ),
              `Versión ${String(confirm.versionNumber)} activa`
            );
            setTarget("");
          }
        }}
      />
    </div>
  );
}
